<script setup>
import { storeToRefs } from 'pinia'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useRestaurantsStore, useUserGroupsStore, useUsersStore, useVarsStore, useCmsContentStore, useAppModeStore, useSupportStore } from '@/stores'
import { useMessagingStore } from '@/stores/messaging.store'
import RaRestaurantCard from '@/layouts/components/RaRestaurantCard.vue'
import { badgeText } from '@/utils/monitoring'

const router = useRouter()

const usersStore = useUsersStore()
let myUser = null
  ; ({ myUser } = storeToRefs(usersStore))

const varsStore = useVarsStore()
let editingGlobalSettings = null
let adminDirectReservationDialogVisible = null
  ; ({ editingGlobalSettings, adminDirectReservationDialogVisible } = storeToRefs(varsStore))

const userGroupsStore = useUserGroupsStore()
let restaurantUserGroups = null
  ; ({ restaurantUserGroups } = storeToRefs(userGroupsStore))

const restaurantsStore = useRestaurantsStore()

const messagingStore = useMessagingStore()
const { restaurantUnreadCount } = storeToRefs(messagingStore)

const supportStore = useSupportStore()

async function openSupportThread() {
  try {
    const conv = await supportStore.getOrCreateSupportConversation()
    if (conv?._id) {
      router.push({ path: '/messages', query: { conv: conv._id } })
    }
  } catch (err) {
    console.error('Failed to open support thread', err)
  }
}

const cmsStore = useCmsContentStore()
const { showCmsEditFab } = storeToRefs(cmsStore)

// Admin Settings: one list, shown A-Z by the label it displays (Kapil 10/3:
// "alphabetical order again"). Labels can come from translations, which the
// CMS can rename, so the order is worked out from the label as shown, and a
// new or renamed item lands in its place on its own.
const { t } = useI18n()

// Admin → Monitoring's badge: open issues that are not muted (Kapil 11/1).
// Read when the menu opens; a failure leaves the badge off, never the menu.
const monitoringOpen = ref(0)
watch(() => myUser.value?.userLevel, async level => {
  if (level !== 'admin')
    return
  try {
    const axios = (await import('@axios')).default
    const { data } = await axios.get('/admin/monitoring/count')

    monitoringOpen.value = data?.data?.open || 0
  } catch (_err) {
    monitoringOpen.value = 0
  }
}, { immediate: true })

const adminMenuItems = computed(() => [
  { key: 'activity', label: 'Activity Feed', icon: 'mdi-pulse', to: '/admin-activity-feed' },
  { key: 'addReservation', label: 'Add Reservation Record', icon: 'mdi-calendar-plus', action: () => { adminDirectReservationDialogVisible.value = true } },
  { key: 'teamAi', label: 'RM Team', icon: 'mdi-shield-star-outline', to: '/ai/admin' },
  { key: 'teamFeed', label: 'RM Team Feed', icon: 'mdi-history', to: '/admin-rm-team-feed' },
  { key: 'annotations', label: 'AI Annotations', icon: 'mdi-comment-check-outline', to: '/admin-annotations' },
  { key: 'conversations', label: 'AI Conversations', icon: 'mdi-robot-outline', to: '/admin-agent-conversations' },
  { key: 'cms', label: t('menu.cmsContent'), icon: 'mdi-file-document-edit-outline', to: '/all-cms-content' },
  {
    key: 'cmsFab',
    label: 'CMS Edit FAB',
    icon: showCmsEditFab.value ? 'mdi-toggle-switch' : 'mdi-toggle-switch-off-outline',
    active: showCmsEditFab.value,
    action: () => cmsStore.toggleCmsEditFab(),
  },
  { key: 'applications', label: 'Applications', icon: 'mdi-form-select', to: '/admin-applications' },
  { key: 'comms', label: 'Comms Outbox', icon: 'mdi-email-fast', to: '/admin-comms' },
  { key: 'connect', label: t('menu.connectMembers'), icon: 'mdi-account-multiple-plus-outline', to: '/admin-connect-members' },
  { key: 'emailTemplates', label: t('menu.emailTemplates'), icon: 'mdi-email-outline', to: '/all-email-templates' },
  { key: 'approvals', label: 'Pending RM Review', icon: 'mdi-check-decagram-outline', to: '/admin-approvals' },
  { key: 'replies', label: 'RM AI replies', icon: 'mdi-message-reply-text-outline', to: '/admin-agent-replies' },
  { key: 'groups', label: t('menu.groupAccounts'), icon: 'mdi-account-group-outline', to: '/all-groups' },
  { key: 'instant', label: t('menu.instantBookings'), icon: 'mdi-flash-outline', to: '/all-platinum-reserved' },
  { key: 'members', label: t('menu.memberAccounts'), icon: 'mdi-account-circle-outline', to: '/all-members' },
  { key: 'highlights', label: 'Member Highlights', icon: 'mdi-format-quote-open', to: '/admin-member-highlights' },
  { key: 'monitoring', label: 'Monitoring', icon: 'mdi-alert-circle-outline', to: '/admin-monitoring', badge: badgeText(monitoringOpen.value) },
  { key: 'updates', label: 'Member Updates', icon: 'mdi-email-newsletter', to: '/admin-member-updates' },
  { key: 'restaurantReview', label: 'Restaurant Data Review', icon: 'mdi-clipboard-check-outline', to: '/admin-restaurant-data-review' },
  { key: 'restaurants', label: t('menu.restaurantDatabase'), icon: 'mdi-silverware-fork-knife', to: '/all-restaurants' },
  { key: 'reports', label: 'Restaurant Reports', icon: 'mdi-file-chart-outline', to: '/admin-reports' },
  { key: 'text', label: 'RM via Text', icon: 'mdi-message-text-outline', to: '/admin-sms-members' },
  { key: 'settings', label: t('menu.settings'), icon: 'mdi-cog-outline', action: () => { editingGlobalSettings.value = true } },
  { key: 'support', label: 'Support Inbox', icon: 'mdi-lifebuoy', to: '/admin-support' },
  { key: 'suppressions', label: 'Suppressions', icon: 'mdi-email-off', to: '/admin-comms-suppressions' },
  { key: 'tags', label: t('menu.tags'), icon: 'mdi-tag-outline', to: '/all-tags' },
].sort((a, b) => a.label.localeCompare(b.label, 'en', { sensitivity: 'base' })))

// Fetch user groups data
let fetched = false
let userGroupCount = 0

watch(myUser, newMyUser => {
  if (newMyUser) {
    if (newMyUser.userLevel != 'admin' && !fetched) {
      userGroupsStore.getMyUserGroups()
      fetched = true
    } else if (userGroupCount != newMyUser.userGroups?.length) {
      userGroupCount = newMyUser.userGroups?.length
      userGroupsStore.getMyUserGroups()
    }
  }
}, { immediate: true })

const groupedRestaurantUserGroups = computed(() => {
  const buckets = restaurantUserGroups.value.reduce((groups, rug) => {
    const key = rug.restaurant?.market || ''
    if (!groups[key]) groups[key] = []
    groups[key].push(rug)

    return groups
  }, {})

  return Object.keys(buckets)
    .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }))
    .map(market => ({
      market,
      restaurants: buckets[market].sort((a, b) =>
        a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }),
      ),
    }))
})

const isRestaurantAdmin = computed(() =>
  myUser.value?.primaryGroupType == 'restaurant' && ['owner', 'admin', 'adminStandard'].includes(myUser.value?.primaryGroupPermission),
)

const appModeStore = useAppModeStore()
const { mode: appMode } = storeToRefs(appModeStore)

// ─── Messaging Settings ───────────────────────────────────────────────────────
const messagingSettings = ref({})
const savingMessagingSettings = ref({})
const messagingSettingsSnack = ref(false)
const messagingSettingsSnackError = ref(false)

watch(restaurantUserGroups, async rugs => {
  if (!rugs || myUser.value?.userLevel === 'admin') return
  const axios = (await import('@axios')).default
  for (const rug of rugs) {
    const id = rug.restaurant?._id
    if (!id || messagingSettings.value[id] !== undefined) continue
    try {
      const { data } = await axios.get(`/restaurant/${id}`)
      const r = data.restaurant || data
      messagingSettings.value = {
        ...messagingSettings.value,
        [id]: {
          allowRestaurantToMessageFans: r.allowRestaurantToMessageFans ?? true,
          allowFanReplies: r.allowFanReplies ?? true,
        },
      }
    } catch {
      // leave undefined — settings section stays hidden for this restaurant
    }
  }
}, { immediate: true })

async function saveMessagingSettings(restaurantId, patch) {
  // Optimistic update
  const previous = { ...messagingSettings.value[restaurantId] }
  messagingSettings.value = {
    ...messagingSettings.value,
    [restaurantId]: { ...messagingSettings.value[restaurantId], ...patch },
  }
  savingMessagingSettings.value = { ...savingMessagingSettings.value, [restaurantId]: true }
  try {
    const axios = (await import('@axios')).default
    await axios.put(`/restaurant/${restaurantId}/messaging-settings`, patch)
    messagingSettingsSnackError.value = false
    messagingSettingsSnack.value = true
  } catch {
    // Revert on error
    messagingSettings.value = {
      ...messagingSettings.value,
      [restaurantId]: previous,
    }
    messagingSettingsSnackError.value = true
    messagingSettingsSnack.value = true
  } finally {
    savingMessagingSettings.value = { ...savingMessagingSettings.value, [restaurantId]: false }
  }
}
</script>

<template>
  <div class="ra-menu-section">
    <!--
      Activity/Bookings — shown only in Member mode. In Restaurant Admin mode,
      /admin/profile's stat tiles are the entry point into reservations-review. 
    -->
    <template v-if="myUser?.userLevel == 'admin' && appMode !== 'restaurantAdmin'">
      <div class="icon-menu-grid">
        <RouterLink
          to="/reservations-review"
          class="icon-menu-item"
        >
          <div class="icon-menu-circle">
            <VIcon
              icon="mdi-calendar-check"
              size="24"
            />
          </div>
          <span class="icon-menu-label">{{ $t('menu.activityBookings') }}</span>
        </RouterLink>
      </div>
    </template>

    <!-- Restaurant Pages -->
    <template v-if="myUser?.userLevel != 'admin' && restaurantUserGroups?.length > 0">
      <div class="icon-menu-grid">
        <RouterLink
          to="/restaurants"
          class="icon-menu-item"
        >
          <div class="icon-menu-circle">
            <VIcon
              icon="mdi-help-circle-outline"
              size="24"
            />
          </div>
          <span class="icon-menu-label">How RM Works &amp; FAQs</span>
        </RouterLink>

        <RouterLink
          to="/admin/messaging"
          class="icon-menu-item"
        >
          <div class="icon-menu-circle">
            <VIcon
              icon="mdi-message-text-outline"
              size="24"
            />
          </div>
          <span class="icon-menu-label">Messages</span>
        </RouterLink>

        <div
          class="icon-menu-item"
          role="button"
          tabindex="0"
          @click="openSupportThread"
          @keydown.enter="openSupportThread"
          @keydown.space.prevent="openSupportThread"
        >
          <div class="icon-menu-circle">
            <VIcon
              icon="mdi-lifebuoy"
              size="24"
            />
          </div>
          <span class="icon-menu-label">Support</span>
        </div>
      </div>

      <div class="section-divider" />
      <div class="section-header">
        {{ $t('menu.restaurantPages') }}
      </div>

      <div class="ra-restaurant-list">
        <template
          v-for="bucket in groupedRestaurantUserGroups"
          :key="bucket.market"
        >
          <div
            v-if="groupedRestaurantUserGroups.length > 1"
            class="ra-restaurant-list__market"
          >
            {{ bucket.market }}
          </div>
          <RaRestaurantCard
            v-for="rug in bucket.restaurants"
            :key="rug._id"
            :restaurant="rug.restaurant"
            :fallback-name="rug.name"
          />
        </template>
      </div>
    </template>

    <!-- Messaging Settings (restaurant admins only) -->
    <!--
      <template v-if="myUser?.userLevel != 'admin' && restaurantUserGroups?.length > 0">
      <div class="section-divider" />
      <div class="section-header">
      Messaging
      </div>
      <div class="messaging-settings-list">
      <div
      v-for="rug in restaurantUserGroups"
      :key="rug._id"
      class="messaging-settings-item"
      >
      <div
      v-if="messagingSettings[rug.restaurant?._id]"
      class="messaging-settings-card"
      >
      <div
      v-if="restaurantUserGroups.length > 1"
      class="messaging-settings-restaurant-name"
      >
      {{ rug.name }}
      </div>
      <div class="messaging-settings-row">
      <div class="messaging-settings-label">
      Allow our restaurant to message fans
      </div>
      <VSwitch
      :model-value="messagingSettings[rug.restaurant._id].allowRestaurantToMessageFans"
      :loading="savingMessagingSettings[rug.restaurant._id]"
      color="primary"
      density="compact"
      hide-details
      @update:model-value="val => saveMessagingSettings(rug.restaurant._id, { allowRestaurantToMessageFans: val })"
      />
      </div>
      <div class="messaging-settings-row">
      <div class="messaging-settings-label">
      Allow fans to reply to our messages
      </div>
      <VSwitch
      :model-value="messagingSettings[rug.restaurant._id].allowFanReplies"
      :loading="savingMessagingSettings[rug.restaurant._id]"
      color="primary"
      density="compact"
      hide-details
      @update:model-value="val => saveMessagingSettings(rug.restaurant._id, { allowFanReplies: val })"
      />
      </div>
      <div class="messaging-settings-muted-hint">
      Even when replies are off, fans can still read your messages.
      </div>
      </div>
      </div>
      </div>
      </template> 
    -->

    <!-- Admin Settings -->
    <template v-if="myUser?.userLevel == 'admin'">
      <div class="section-divider" />
      <div class="section-header">
        {{ $t('menu.adminSettings') }}
      </div>

      <div class="icon-menu-grid">
        <template
          v-for="item in adminMenuItems"
          :key="item.key"
        >
          <RouterLink
            v-if="item.to"
            :to="item.to"
            class="icon-menu-item"
            :aria-label="item.badge ? `${item.label}, ${item.badge} open` : undefined"
          >
            <div class="icon-menu-circle">
              <VIcon
                :icon="item.icon"
                size="24"
              />
              <span
                v-if="item.badge"
                class="icon-menu-badge"
                aria-hidden="true"
              >{{ item.badge }}</span>
            </div>
            <span class="icon-menu-label">{{ item.label }}</span>
          </RouterLink>
          <div
            v-else
            class="icon-menu-item"
            role="button"
            tabindex="0"
            @click="item.action()"
            @keydown.enter="item.action()"
            @keydown.space.prevent="item.action()"
          >
            <div
              class="icon-menu-circle"
              :class="{ active: item.active }"
            >
              <VIcon
                :icon="item.icon"
                size="24"
              />
            </div>
            <span class="icon-menu-label">{{ item.label }}</span>
          </div>
        </template>
      </div>
    </template>

    <!-- Messaging settings save toast -->
    <VSnackbar
      v-model="messagingSettingsSnack"
      :timeout="2500"
      :color="messagingSettingsSnackError ? 'error' : 'success'"
    >
      {{ messagingSettingsSnackError ? 'Failed to save settings.' : 'Settings saved.' }}
    </VSnackbar>
  </div>
</template>

<style lang="scss" scoped>
.ra-menu-section {
  align-self: stretch;
  width: 100%;
}

.icon-menu-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: var(--ds-space-1) 0;
  padding: var(--ds-space-4) var(--ds-space-2) var(--ds-space-3);
}

.icon-menu-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--ds-space-2);
  padding: var(--ds-space-3) var(--ds-space-2);
  border-radius: var(--ds-radius-md);
  cursor: pointer;
  text-decoration: none;
  color: inherit;
  transition: background-color var(--ds-duration-normal);

  &:hover {
    background-color: rgba(0, 0, 0, 0.04);
  }

  &:hover .icon-menu-circle {
    border-color: var(--ds-brand-primary);
    color: var(--ds-brand-primary);
  }
}

.icon-menu-badge {
  position: absolute;
  top: -4px;
  right: -4px;
  min-width: 20px;
  height: 20px;
  padding: 0 6px;
  border-radius: 10px;
  background: rgb(var(--v-theme-error));
  color: #fff;
  font-size: 12px;
  font-weight: 600;
  line-height: 20px;
  text-align: center;
}

.icon-menu-circle {
  position: relative;
  width: 52px;
  height: 52px;
  border-radius: var(--ds-radius-full);
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgb(248, 249, 250);
  border: 1.5px solid var(--ds-border-default);
  color: var(--ds-text-secondary);
  transition: all var(--ds-duration-normal);

  &.active {
    background-color: var(--ds-brand-primary-light, #e9f4f3);
    border-color: var(--ds-brand-primary, #4db6ac);
    color: var(--ds-brand-primary-dark, #3F998F);
  }
}

.icon-menu-label {
  font-size: var(--ds-text-xs);
  font-weight: 500;
  color: var(--ds-text-secondary);
  text-align: center;
  line-height: 1.3;
  max-width: 90px;
}

.section-divider {
  width: 100%;
  height: 1px;
  background-color: rgba(0, 0, 0, 0.08);
  margin: var(--ds-space-3) 0 0;
}

.section-header {
  padding: var(--ds-space-3) var(--ds-space-4) var(--ds-space-2);
  font-size: 13px;
  font-weight: 600;
  color: rgb(102, 102, 102);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.ra-restaurant-list {
  display: flex;
  flex-direction: column;
  gap: var(--ds-space-2);
  padding: var(--ds-space-2) var(--ds-space-4) var(--ds-space-3);
}

.ra-restaurant-list__market {
  font-size: 12px;
  font-weight: 500;
  color: rgb(102, 102, 102);
  letter-spacing: 0.3px;
  margin-top: var(--ds-space-2);
}

.ra-restaurant-list__market:first-child {
  margin-top: 0;
}

.messaging-settings-list {
  padding: var(--ds-space-2) var(--ds-space-4) var(--ds-space-3);
  display: flex;
  flex-direction: column;
  gap: var(--ds-space-3);
}

.messaging-settings-card {
  background: rgb(var(--v-theme-surface));
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 8px;
  padding: var(--ds-space-3) var(--ds-space-4);
}

.messaging-settings-restaurant-name {
  font-size: 13px;
  font-weight: 600;
  color: var(--ds-text-primary);
  margin-bottom: var(--ds-space-2);
}

.messaging-settings-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: var(--ds-space-1) 0;
}

.messaging-settings-label {
  font-size: 14px;
  color: var(--ds-text-secondary);
}

.messaging-settings-muted-hint {
  font-size: 12px;
  color: var(--ds-text-disabled, rgba(0, 0, 0, 0.38));
  margin-top: var(--ds-space-2);
}
</style>
